// BinaryFeedService: minimal implementation wrapping BinaryAPI for IFeedService contract
// This is a lightweight adapter; full Feed logic (pagination, delayed streams, etc.) remains in legacy Feed class for now

import { TGranularity } from '../types/granularity.types';
import { TQuote } from '../types/quote.types';
import {
    IFeedService,
    FetchHistoryParams,
    FetchHistoryResult,
    LiveSubscriptionParams,
    UnsubscribeFn,
} from './IFeedService';

// External dependencies from legacy code (to be injected)
export type BinaryAPILike = {
    getTickHistory(params: any): Promise<any>;
    subscribeTickHistory(params: any, callback: (response: any) => void): void;
    forget(params: { symbol: string; granularity: TGranularity }): void;
};

export type TickHistoryFormatterLike = {
    formatHistory(response: any): TQuote[];
};

export interface BinaryFeedServiceOptions {
    api: BinaryAPILike;
    formatter: TickHistoryFormatterLike;
    isFeedUnavailable?: (symbol: string) => boolean;
    getDelayedMinutes?: (symbol: string) => number;
}

export class BinaryFeedService implements IFeedService {
    private _api: BinaryAPILike;
    private _formatter: TickHistoryFormatterLike;
    private _isFeedUnavailableFn?: (symbol: string) => boolean;
    private _getDelayedMinutesFn?: (symbol: string) => number;
    private _activeSubscriptions: Map<string, { callback: (tick: TQuote) => void }> = new Map();

    constructor(opts: BinaryFeedServiceOptions) {
        this._api = opts.api;
        this._formatter = opts.formatter;
        this._isFeedUnavailableFn = opts.isFeedUnavailable;
        this._getDelayedMinutesFn = opts.getDelayedMinutes;
    }

    async fetchHistory(params: FetchHistoryParams): Promise<FetchHistoryResult> {
        const { symbol, granularity, start, end, count } = params;
        const request: any = {
            symbol,
            granularity: granularity || undefined,
            start,
            end: end || 'latest',
            count: count || 1000,
            adjust_start_time: 1,
        };

        if (start) {
            delete request.count; // if start is provided, count is ignored
        }

        try {
            const response = await this._api.getTickHistory(request);
            if (response.error) {
                return {
                    quotes: [],
                    error: response.error.message || 'Unknown error',
                };
            }
            const quotes = this._formatter.formatHistory(response);
            return { quotes };
        } catch (err: any) {
            return {
                quotes: [],
                error: err.message || 'Fetch history failed',
            };
        }
    }

    async subscribeLive(params: LiveSubscriptionParams): Promise<UnsubscribeFn> {
        const { symbol, granularity, onTick, onError } = params;
        const key = this._getKey(symbol, granularity);

        // Unsubscribe previous if exists
        if (this._activeSubscriptions.has(key)) {
            this.unsubscribe(symbol, granularity);
        }

        const request: any = {
            symbol,
            granularity: granularity || undefined,
            subscribe: 1,
            end: 'latest',
            count: 1000,
            adjust_start_time: 1,
        };

        const callback = (response: any) => {
            if (response.error) {
                if (onError) {
                    onError({ message: response.error.message, code: response.error.code });
                }
                return;
            }
            // Format tick/candle from subscription response
            const quotes = this._formatter.formatHistory(response);
            if (quotes.length > 0) {
                const latestTick = quotes[quotes.length - 1];
                // Debug: log live tick arrival
                try { console.debug('BinaryFeedService live tick:', symbol, granularity, latestTick); } catch {}
                onTick(latestTick);
            }
        };

        this._activeSubscriptions.set(key, { callback });
        this._api.subscribeTickHistory(request, callback);

        return () => this.unsubscribe(symbol, granularity);
    }

    unsubscribe(symbol: string, granularity: TGranularity): void {
        const key = this._getKey(symbol, granularity);
        if (this._activeSubscriptions.has(key)) {
            this._api.forget({ symbol, granularity });
            this._activeSubscriptions.delete(key);
        }
    }

    unsubscribeAll(): void {
        for (const [key] of this._activeSubscriptions) {
            const [symbol, gran] = key.split('-');
            this.unsubscribe(symbol, Number(gran) as TGranularity);
        }
    }

    isFeedAvailable(symbol: string): boolean {
        if (this._isFeedUnavailableFn) {
            return !this._isFeedUnavailableFn(symbol);
        }
        return true; // assume available if no check provided
    }

    getDelayMinutes(symbol: string): number {
        return this._getDelayedMinutesFn?.(symbol) || 0;
    }

    private _getKey(symbol: string, granularity: TGranularity): string {
        return `${symbol}-${granularity}`;
    }
}

export default BinaryFeedService;
